import { useState } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { useSnackbar } from 'notistack'
import { getFeature } from '@paintscout/util/builder'
import type { UploadOptions } from '@paintscout/util/s3'
import { compressFile, uploadFile } from '@paintscout/util/s3'

import { useClientOptions } from '../../providers/ClientOptionsProvider'

export interface ImageUploadResult {
  src: string
  cloudinaryPublicId: string
  s3PublicKey: string
  format: string
  pages: number
  width: number
  height: number
  type: string
}

export interface UseImageUploadArgs {
  maxWidth?: number
  maxHeight?: number
  uploadOptions?: UploadOptions
  onUpload: (args: ImageUploadResult) => any
  /**
   * Opt-in multi-file upload (#23709). When set, a drop of several files uploads them all and
   * reports them in one call, in drop order, and `onUpload` is not called. Files that fail to
   * upload are left out. Without it a drop still uses only the first file, as it always has.
   */
  onUploadMultiple?: (results: ImageUploadResult[]) => any
  /**
   * If provided, a dropped file is handed off to this callback and NOT uploaded.
   * (Used by the core UploadImage's deferred-upload flow.)
   */
  onFileSelected?: (file: File) => void
  /**
   * Message shown in the error snackbar. Callers that translate should pass a
   * pre-translated string.
   */
  errorMessage?: string
  /**
   * Override the company/feature-flag used for the upload. When omitted, both fall
   * back to the values from {@link useClientOptions} (the ambient ClientOptionsProvider).
   */
  companyId?: string
  s3UploadFeatureEnabled?: boolean
  /**
   * LaunchDarkly `s3MediaUpload` flag value. When true, PDFs/videos are also
   * routed to S3 instead of being force-routed to Cloudinary (see uploadFile.ts).
   */
  s3MediaUploadEnabled?: boolean
}

export interface UseImageUploadResult {
  loading: boolean
  handleDrop: (files: File[]) => Promise<void>
}

/**
 * Shared upload orchestration for the core and presentation UploadImage components (#21305):
 * compress → upload → onUpload, loading state, error snackbar, and provider fallback for the
 * company/feature-flag. Keeping this in one place removes the duplicated, upload-sensitive
 * logic while letting each component render its own preview.
 */
export function useImageUpload({
  maxWidth,
  maxHeight,
  uploadOptions,
  onUpload,
  onUploadMultiple,
  onFileSelected,
  errorMessage = 'Unable to upload image',
  companyId,
  s3UploadFeatureEnabled,
  s3MediaUploadEnabled
}: UseImageUploadArgs): UseImageUploadResult {
  const { enqueueSnackbar } = useSnackbar()
  const apolloClient = useApolloClient()
  const { options, clientId } = useClientOptions()
  const [loading, setLoading] = useState(false)

  const resolvedCompanyId = companyId ?? clientId
  const resolvedS3UploadFeatureEnabled =
    s3UploadFeatureEnabled ?? getFeature({ options, path: 's3ImageUpload.enabled' })

  async function uploadOne(rawFile: File): Promise<ImageUploadResult> {
    const file = await compressFile({ file: rawFile, maxHeight, maxWidth })
    const finalUploadOptions = {
      companyId: resolvedCompanyId,
      s3UploadFeatureEnabled: resolvedS3UploadFeatureEnabled,
      s3MediaUploadEnabled,
      ...uploadOptions
    }
    const { format, pages, width, height, type, src, cloudinaryPublicId, s3PublicKey } = await uploadFile({
      apolloClient,
      file,
      uploadOptions: finalUploadOptions
    })

    return { src, format, pages, cloudinaryPublicId, s3PublicKey, width, height, type }
  }

  async function handleDrop(files: File[]) {
    if (!files.length) return
    if (onFileSelected) {
      onFileSelected(files[0])
      return
    }

    if (onUploadMultiple) {
      setLoading(true)
      const results: ImageUploadResult[] = []
      let failed = 0
      try {
        // Sequential, like the other multi-file upload path (useFileDropArea): one file's failure
        // must not lose the ones that already uploaded.
        for (const rawFile of files) {
          try {
            results.push(await uploadOne(rawFile))
          } catch (e) {
            console.error(e)
            failed++
          }
        }
        // A partial failure is invisible otherwise — the files that worked appear, and nothing says
        // the others are missing. The generic message only fits when every file failed.
        if (failed) {
          const message = results.length ? `${failed} of ${files.length} files failed to upload` : errorMessage
          enqueueSnackbar(message, { variant: 'error' })
        }
        if (results.length) onUploadMultiple(results)
      } finally {
        // The loop swallows a per-file failure, but the snackbar and the caller's own callback can
        // still throw — without this the spinner would never clear.
        setLoading(false)
      }
      return
    }

    try {
      setLoading(true)
      onUpload(await uploadOne(files[0]))
    } catch (e) {
      console.error(e)
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
    setLoading(false)
  }

  return { loading, handleDrop }
}
